import { Loader2, PhoneMissed, PhoneOff, Radio, type LucideIcon } from 'lucide-react';
import type { BadgeTone } from '@/lib/calls/format';
import type { ConferenceLog, ConferenceLogParticipant } from './callLogTypes';
import type { ConferenceStatus } from './types';

/**
 * ONE row per person. A member is fork-dialed across every device, so the raw
 * record has several legs per person (a joined one plus failed/never-answered
 * siblings). Collapse to the leg that best represents their participation — the
 * longest one, preferring a leg that actually joined — so the history shows each
 * person once, not the same name three times with different "activity".
 */
export function dedupeLogParticipants(
  participants: ConferenceLogParticipant[],
): ConferenceLogParticipant[] {
  const score = (p: ConferenceLogParticipant) => (p.durationSec || 0) + (p.joinedAt ? 0.5 : 0);
  const best = new Map<string, ConferenceLogParticipant>();
  for (const p of participants) {
    const key = p.userId ?? p.e164 ?? p.callControlId;
    const cur = best.get(key);
    if (!cur || score(p) > score(cur)) best.set(key, p);
  }
  return [...best.values()];
}

/** Did this person actually make it into the room (vs. never answered)? */
export function participantJoined(p: ConferenceLogParticipant): boolean {
  return p.durationSec > 0 || p.joinedAt != null;
}

/** Status → tone + label + icon, shared by the conference row and detail header. */
export function conferenceStatusMeta(status: ConferenceStatus): {
  tone: BadgeTone;
  label: string;
  Icon: LucideIcon;
} {
  switch (status) {
    case 'ACTIVE':
      return { tone: 'positive', label: 'Live', Icon: Radio };
    case 'INITIATING':
      return { tone: 'warning', label: 'Connecting', Icon: Loader2 };
    case 'FAILED':
      return { tone: 'danger', label: 'Failed', Icon: PhoneMissed };
    case 'ENDED':
    default:
      return { tone: 'neutral', label: 'Ended', Icon: PhoneOff };
  }
}

/** Wall-clock room duration (start → end) — the intuitive "how long was the call". */
export function roomDurationSec(log: Pick<ConferenceLog, 'startedAt' | 'endedAt'>): number {
  if (!log.startedAt || !log.endedAt) return 0;
  const ms = new Date(log.endedAt).getTime() - new Date(log.startedAt).getTime();
  return ms > 0 ? Math.round(ms / 1000) : 0;
}

export function conferenceTitle(log: Pick<ConferenceLog, 'type'>): string {
  return log.type === 'INTERNAL_1_1' ? 'Internal call' : 'Team conference';
}
